import { commitMutation, Environment } from "react-relay";
import { GraphQLTaggedNode, MutationConfig, MutationParameters } from "relay-runtime";

export function commitMutationWithErrorHandling<
    TOperation extends { response: any }
>(
    environment: Environment,
    config: MutationConfig<TOperation>,
    addError: (error: any) => void,
    options?: {
        suppressGlobalErrorHandling?: boolean;
        customErrorMessage?: string;
    }
): void {
    const { onCompleted, onError, ...relayConfig } = config;
    const { suppressGlobalErrorHandling = false, customErrorMessage } =
        options || {};

    commitMutation(environment, {
        ...relayConfig,
        onCompleted: (response, errors) => {
            if (errors && errors.length > 0 && !suppressGlobalErrorHandling) {
                errors.forEach((error) => {
                    addError({
                        message:
                            customErrorMessage ||
                            error.message ||
                            "An unknown GraphQL error occurred",
                        type: "relay",
                        details: {
                            originalMessage: error.message,
                            path: error.path,
                            locations: error.locations,
                            extensions: error.extensions,
                        },
                    });
                });
            }

            if (onCompleted) {
                onCompleted(response, errors);
            }
        },
        onError: (error) => {
            if (!suppressGlobalErrorHandling) {
                addError({
                    message:
                        customErrorMessage ||
                        error.message ||
                        "A network error occurred",
                    type: "network",
                    details: {
                        originalMessage: error.message,
                        name: error.name,
                        stack: error.stack,
                    },
                });
            }

            if (onError) {
                onError(error);
            }
        },
    });
}

/**
 * Commits a mutation and returns a promise, for several mutations that must run one after the
 * other. Errors are reported like commitMutationWithErrorHandling does and also reject the promise.
 */
export function commitMutationAsPromise<TOperation extends MutationParameters>(
    environment: Environment,
    mutation: GraphQLTaggedNode,
    variables: TOperation["variables"],
    addError: (error: any) => void,
    customErrorMessage: string
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        commitMutationWithErrorHandling<TOperation>(
            environment,
            {
                mutation,
                variables,
                onCompleted: (_, errors) => (errors && errors.length > 0 ? reject(errors) : resolve()),
                onError: reject,
            },
            addError,
            { customErrorMessage }
        );
    });
}
